import { LIST_URL_GROUPS_NAME } from "@/lib/ai/tools/list-url-groups/constants";
import type { ListUrlGroupsOutput } from "@/lib/ai/tools/list-url-groups/server";
import { defineDatasetSpec } from "@/lib/ai/visualization/data/adapters/adapter-contract";
import {
  buildListTableDataset,
  createTableOnlyProfile,
  toListNumber,
  toListText,
  toListTextList,
} from "@/lib/ai/visualization/data/adapters/dataset-normalization";
import type {
  VizDataset,
  VizDatasetRow,
} from "@/lib/ai/visualization/data/schema";

const URL_GROUPS_DATASET_SPEC = defineDatasetSpec({
  id: `${LIST_URL_GROUPS_NAME}-table`,
  profile: createTableOnlyProfile({ datasetFamily: "list.urlGroups" }),
  fields: [
    { key: "projectType", role: "dim", type: "string" },
    { key: "analyticsId", role: "dim", type: "string" },
    { key: "urlGroupId", role: "dim", type: "string" },
    { key: "urlGroupName", role: "dim", type: "string" },
    { key: "description", role: "dim", type: "string" },
    { key: "operator", role: "dim", type: "string" },
    { key: "patternCount", role: "metric", type: "number", unit: "count" },
    { key: "patternsPreview", role: "dim", type: "string" },
  ] as const,
  defaults: { category: "urlGroupName", value: "patternCount" },
  notes: "Table dataset normalized from list_url_groups output.",
});

function toUrlGroupRows(output: Partial<ListUrlGroupsOutput>): VizDatasetRow[] {
  const groups = Array.isArray(output.urlGroups) ? output.urlGroups : [];

  return groups.map((group) => {
    const patterns = Array.isArray(group.patterns) ? group.patterns : [];
    const normalizedPatterns = patterns.filter(
      (pattern): pattern is NonNullable<typeof pattern> => pattern != null,
    );
    return {
      projectType: toListText(output.projectType),
      analyticsId: toListText(output.analyticsId),
      urlGroupId: toListText(group.id),
      urlGroupName: toListText(group.name),
      description: toListText(group.description),
      operator: toListText(group.op),
      patternCount: toListNumber(normalizedPatterns.length),
      patternsPreview: toListTextList(
        normalizedPatterns.map(
          (pattern) => `${pattern.type}:${pattern.pattern}`,
        ),
      ),
    };
  });
}

/**
 * list_url_groups 出力を table-only dataset へ正規化する。
 */
export function buildListUrlGroupsDatasets(
  output: unknown,
  source: VizDataset["source"],
): VizDataset[] {
  const typedOutput =
    typeof output === "object" && output !== null
      ? (output as Partial<ListUrlGroupsOutput>)
      : {};
  const rows = toUrlGroupRows(typedOutput);
  const dataset = buildListTableDataset({
    id: URL_GROUPS_DATASET_SPEC.id,
    datasetFamily: URL_GROUPS_DATASET_SPEC.profile.datasetFamily,
    variant: URL_GROUPS_DATASET_SPEC.profile.variant,
    source,
    rows,
    fields: URL_GROUPS_DATASET_SPEC.fields,
    defaults: URL_GROUPS_DATASET_SPEC.defaults,
    notes: URL_GROUPS_DATASET_SPEC.notes,
  });

  return dataset ? [dataset] : [];
}
